import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EntityDialog from '../../EntityDialog';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { addPackage } from '../../hooks/usePackages';
import { getFacilitiesByOrganization } from '../../hooks/useFacilities';
import { getAllMarkets } from '../../hooks/useMarkets';
import { getProductsListWithStatus } from '../../hooks/useProducts';

export default function AddPackageContainer() {
  const navigate = useNavigate();
  const { org } = useLegacyAppSession();
  const [products, setProducts] = useState<any[]>([]);
  const [facilities, setFacilities] = useState<any[]>([]);
  const [markets, setMarkets] = useState<any[]>([]);

  useEffect(() => {
    if (!org?.id) return;

    let cancelled = false;

    Promise.allSettled([
      // New packages must select from current products, not historical records.
      getProductsListWithStatus({ organizationId: org.id, active: true, page: 0, pageSize: 1000, includeBrandless: true }),
      getFacilitiesByOrganization(org.id, 0, 500),
      getAllMarkets(org.id),
    ])
      .then(([productResults, facilityResults, marketResults]) => {
        if (cancelled) return;

        const hadError = [productResults, facilityResults, marketResults].some(
          (result) => result.status === 'rejected',
        );
        const hadPartialProducts = productResults.status === 'fulfilled' && !productResults.value.complete;

        setProducts(productResults.status === 'fulfilled' ? productResults.value.products || [] : []);
        setFacilities(
          facilityResults.status === 'fulfilled' ? facilityResults.value?.data || [] : [],
        );
        setMarkets(marketResults.status === 'fulfilled' ? marketResults.value || [] : []);

        if (hadError) {
          showToastError('Package setup data could not be loaded.');
        } else if (hadPartialProducts) {
          showToastError('Some product options could not be loaded.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [org?.id]);

  const productOptions = useMemo(
    () => (products || []).map((product) => ({ label: product.name, value: String(product.id) })),
    [products]
  );
  const facilityOptions = useMemo(
    () => (facilities || []).map((facility) => ({ label: facility.name, value: String(facility.id) })),
    [facilities]
  );
  const marketOptions = useMemo(
    () => (markets || []).map((market) => ({ label: market.name || market.code, value: String(market.id) })),
    [markets]
  );

  return (
    <EntityDialog
      title="Create Package"
      contextLabel="Packages"
      fields={[
        { name: 'packageId', label: 'Id', required: true, placeholder: '1A4xxxxxxxxxxxxxxxxxxxxx' },
        { name: 'productId', label: 'Product', type: 'select', options: productOptions, required: true },
        { name: 'qty', label: 'Quantity', type: 'number', inputType: 'number', required: true },
        {
          name: 'status',
          label: 'Status',
          type: 'select',
          options: ['Active', 'Pending Print', 'Archived'],
          required: true,
        },
        { name: 'marketId', label: 'Market', type: 'select', options: marketOptions, required: true },
        { name: 'facilityId', label: 'Facility License', type: 'select', options: facilityOptions, required: true },
      ]}
      initialValues={{
        packageId: '',
        productId: productOptions[0]?.value || '',
        qty: 0,
        status: 'Active',
        marketId: marketOptions[0]?.value || '',
        facilityId: facilityOptions[0]?.value || '',
      }}
      onClose={() => navigate('/packages', { replace: true })}
      onSave={async (payload) => {
        if (!org?.id) return;
        try {
          await addPackage({
            organizationId: org.id,
            packageId: payload.packageId,
            productId: payload.productId ? Number(payload.productId) : null,
            quantity: Number(payload.qty) || 0,
            status: payload.status,
            marketId: payload.marketId ? Number(payload.marketId) : null,
            facilityId: payload.facilityId ? Number(payload.facilityId) : null,
            packagedDate: null,
          });
          showToastSuccess('Package successfully created.');
          navigate('/packages', { replace: true });
        } catch (error) {
          showToastError('Package could not be saved.');
          console.error(error);
        }
      }}
    />
  );
}
